/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1693872000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Tue Sep 05 2023 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "facebook-conversions",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to Facebook Conversions API",
          "depth": 0,
          "id": "connect-to-facebook-conversions-api"
        }, {
          "value": "Sync configuration",
          "depth": 0,
          "id": "sync-configuration"
        }, {
          "value": "Syncing events",
          "depth": 1,
          "id": "syncing-events"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      ul: "ul",
      li: "li",
      img: "img",
      h3: "h3",
      code: "code",
      h4: "h4"
    }, _provideComponents(), props.components), {EventSyncWarning, LiveDebugger, SyncAlerts} = _components;
    if (!EventSyncWarning) _missingMdxReference("EventSyncWarning", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            }), _jsx(_components.th, {
              children: "API Reference"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Events"
              })
            }), _jsx(_components.td, {
              children: "Sync conversion events to Facebook's (Meta) Conversions API"
            }), _jsx(_components.td, {
              children: "Insert"
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "https://developers.facebook.com/docs/marketing-api/conversions-api/",
                children: "Conversions API reference"
              })
            })]
          })
        })]
      }), "\n", _jsxs(_components.p, {
        children: ["For more information about sync modes, refer to the ", _jsx(_components.a, {
          href: "/syncs/types-and-modes#sync-modes",
          children: "sync modes"
        }), " docs."]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-facebook-conversions-api",
        children: "Connect to Facebook Conversions API"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "Facebook Conversions"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". You can then authenticate Hightouch to the Facebook Conversions API by entering the following required fields into Hightouch:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Pixel ID"
          }), " or ", _jsx(_components.strong, {
            children: "Database ID"
          })]
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "Access Token"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["You can find these credentials by going to Facebook's ", _jsx(_components.a, {
          href: "https://business.facebook.com/events_manager2/",
          children: "Events Manager"
        }), " and opening the ", _jsx(_components.strong, {
          children: "Data Sources"
        }), " tab. Select the pixel you want to send events to and open the ", _jsx(_components.strong, {
          children: "Settings"
        }), " tab. The Pixel ID is available under ", _jsx(_components.strong, {
          children: "Details"
        }), "."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-facebook-conversion-pixel.png",
          alt: "Pixel ID in Facebook Events Manager"
        })
      }), "\n", _jsxs(_components.p, {
        children: ["For the access token, click ", _jsx(_components.strong, {
          children: "Generate access token"
        }), " in the ", _jsx(_components.strong, {
          children: "Conversions API"
        }), " section."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-facebook-conversion-token.png",
          alt: "Access token in Facebook Events Manager"
        })
      }), "\n", _jsx(_components.h2, {
        id: "sync-configuration",
        children: "Sync configuration"
      }), "\n", _jsxs(_components.p, {
        children: ["Once you've set up your Facebook Conversions destination and have a ", _jsx(_components.a, {
          href: "/getting-started/concepts#models",
          children: "model"
        }), " to pull data from, you can set up your sync configuration to begin syncing data. Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/syncs",
          children: [_jsx(_components.strong, {
            children: "Syncs"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add sync"
        }), " button to begin. Then, select the relevant model and the Facebook Conversions destination you want to sync to."]
      }), "\n", _jsx(_components.h3, {
        id: "syncing-events",
        children: "Syncing events"
      }), "\n", _jsx(_components.p, {
        children: "The Conversions API requires the following event parameters:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "event_name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.code, {
            children: "event_time"
          })
        }), "\n", _jsxs(_components.li, {
          children: ["at least one ", _jsxs(_components.a, {
            href: "https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters",
            children: ["customer information (", _jsx(_components.code, {
              children: "user_data"
            }), ") parameter"]
          })]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: "The sync configuration form ensures all these are set and provides some additional options."
      }), "\n", _jsx(EventSyncWarning, {}), "\n", _jsx(_components.h4, {
        id: "event-name",
        children: "Event name"
      }), "\n", _jsxs(_components.p, {
        children: ["Providing an event name is required to send an event to the Conversions API. You can either provide a static value or select to ", _jsx(_components.strong, {
          children: "use a column"
        }), " from your model. Hightouch syncs the static or column value as the ", _jsx(_components.code, {
          children: "event_name"
        }), " parameter the Conversions API requires."]
      }), "\n", _jsx(_components.h4, {
        id: "event-timestamp",
        children: "Event timestamp"
      }), "\n", _jsxs(_components.p, {
        children: ["You can optionally select a column that contains timestamps of when events occurred. If this field is empty, Hightouch uses the time the event arrives at the server. Hightouch syncs the timestamp as the ", _jsx(_components.code, {
          children: "event_time"
        }), " parameter the Conversions API requires."]
      }), "\n", _jsxs(_components.p, {
        children: ["If you select a column, it should be in ", _jsx(_components.a, {
          href: "https://www.iso.org/iso-8601-date-and-time-format.html",
          children: "ISO 8601"
        }), " format. Hightouch automatically converts the data to the format Facebook expects."]
      }), "\n", _jsx(_components.h4, {
        id: "field-mapping",
        children: "Field mapping"
      }), "\n", _jsx(_components.p, {
        children: "Field mapping is where you select which customer information parameters or other event parameters you want to send to the Conversions API."
      }), "\n", _jsxs(_components.p, {
        children: ["The Conversions API requires you to include at least one ", _jsx(_components.a, {
          href: "https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters",
          children: _jsx(_components.code, {
            children: "user_data"
          })
        }), " parameter in your event payload, so be sure to map one here. Facebook uses this field to attribute a conversion to a particular user. You can also include any other user data fields you have to help increase ", _jsx(_components.a, {
          href: "https://www.facebook.com/business/help/765081237991954?id=818859032317965",
          children: "event match quality"
        }), "."]
      }), "\n", _jsxs(_components.p, {
        children: ["Refer to ", _jsx(_components.a, {
          href: "https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/customer-information-parameters/",
          children: "Facebook's developer documentation"
        }), " for more information on available ", _jsx(_components.code, {
          children: "user_data"
        }), " parameters."]
      }), "\n", _jsxs(_components.p, {
        children: ["Columns mapped to custom fields are sent in the ", _jsx(_components.code, {
          children: "custom_data"
        }), " parameter in the event payload."]
      }), "\n", _jsx(_components.h4, {
        id: "advanced-analytics",
        children: "Advanced analytics"
      }), "\n", _jsx(_components.p, {
        children: "To send data through the Conversions API to the Advanced Measurement API (AMAPI), map the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "data_processing_options"
          }), ": an array of strings like ", _jsx(_components.code, {
            children: "[\"AMO\"]"
          })]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "advanced_measurement_table"
          }), ": a string"]
        }), "\n"]
      }), "\n", _jsx(_components.h4, {
        id: "action-source",
        children: "Action source"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.a, {
          href: "https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/server-event#action-source",
          children: _jsx(_components.code, {
            children: "action_source"
          })
        }), " parameter allows you to specify where your conversions occurred, for example, over email, on your website, etc. Knowing where your events took place helps ensure your ads go to the right people. We recommend you include this as part of your field mapping."]
      }), "\n", _jsx(_components.p, {
        children: "In case you haven't mapped this field or know that not all rows in your data include it, you can optionally set a fallback action source."
      }), "\n", _jsx(_components.h4, {
        id: "test-event-codes",
        children: "Test event codes"
      }), "\n", _jsx(_components.p, {
        children: "Test event codes are special codes you can include in your event payload to simulate conversion events. This allows you to test your API implementation without actually recording real conversion data."
      }), "\n", _jsxs(_components.p, {
        children: ["You can optionally enter a test event code as part of your sync configuration. You can find your test event code in your Facebook ", _jsx(_components.a, {
          href: "https://business.facebook.com/events_manager2/",
          children: "Events Manager"
        }), " in the ", _jsx(_components.strong, {
          children: "Test events"
        }), " tab."]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "destinations/destination-facebook-conversion-test-event-code.png",
          alt: "Test event code in Facebook Events Manager"
        })
      }), "\n", _jsx(_components.p, {
        children: "Make sure to remove the test event code if you're running a production sync."
      }), "\n", _jsx(_components.h4, {
        id: "hashing",
        children: "Hashing"
      }), "\n", _jsx(_components.p, {
        children: "By default, Hightouch automatically hashes the following fields:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "email"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "phone"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "gender"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "dob"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "last name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "first name"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "city"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "state"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "zip"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.strong, {
            children: "country"
          })
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["If you want to turn hashing off, select ", _jsx(_components.strong, {
          children: "No"
        }), " under ", _jsx(_components.strong, {
          children: "Would you like Hightouch to automatically detect if your PII data requires hashing?"
        }), "."]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(_components.h4, {
        id: "facebook-sdk",
        children: "Facebook SDK"
      }), "\n", _jsx(_components.p, {
        children: "When setting up a new datasource for CAPI, if you don't want to use the Facebook SDK in your app, choose “Web” (even if the data would include app events as well). This eliminates the need to install the SDK."
      }), "\n", _jsxs(_components.p, {
        children: ["In this case, use ", _jsx(_components.strong, {
          children: "Dataset ID"
        }), " instead of ", _jsx(_components.strong, {
          children: "Pixel ID"
        }), " when configuring the destination."]
      }), "\n", _jsx(_components.h4, {
        id: "app-data",
        children: "App data"
      }), "\n", _jsxs(_components.p, {
        children: ["To send app data in your CAPI events, include ", _jsx(_components.a, {
          href: "https://developers.facebook.com/docs/marketing-api/conversions-api/parameters/app-data#",
          children: "app data parameters"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
